import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, Menu, X, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import logo from "@/assets/arcondes-logo.png";

const navKeys = [
  { key: "home", path: "/" },
  { key: "localLaw11", path: "/local-law-11" },
  { key: "services", path: "/services" },
  { key: "projects", path: "/projects" },
  { key: "about", path: "/about" },
  { key: "blog", path: "/blog" },
  { key: "buildingReport", path: "/building-report" },
  { key: "contact", path: "/contact" },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation("common");
  const prefix = i18n.language === "es" ? "/es" : "";

  const localizedPath = (path: string) => `${prefix}${path}`;

  const isActive = (path: string) => {
    const fullPath = localizedPath(path);
    return location.pathname === fullPath || location.pathname === path;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Urgency bar */}
      <div className="bg-danger hidden md:block">
        <div className="container-max flex items-center justify-center h-8 px-4 sm:px-6 lg:px-8 text-white">
          <div className="flex items-center gap-2 text-xs font-semibold">
            <AlertTriangle className="h-3.5 w-3.5" />
            <span>{t("nav.urgencyBar")}</span>
            <Link to={localizedPath("/local-law-11")} className="underline ml-1 hover:text-white/80">{t("nav.checkYourDeadline")}</Link>
          </div>
        </div>
      </div>

      {/* Yellow utility bar */}
      <div className="bg-brand-yellow hidden sm:block">
        <div className="container-max flex items-center justify-between h-8 px-4 sm:px-6 lg:px-8 text-brand-yellow-foreground">
          <div className="flex items-center gap-6 text-xs font-medium">
            <span>{t("nav.locationBar")}</span>
          </div>
          <div className="flex items-center gap-5 text-xs font-medium">
            <a href="mailto:info@arcondes.com" className="flex items-center gap-1.5 hover:underline">
              <Mail className="h-3 w-3" />
              info@arcondes.com
            </a>
            <a href="tel:+13476131717" className="flex items-center gap-1.5 hover:underline">
              <Phone className="h-3 w-3" />
              347-613-1717
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="container-max flex items-center justify-between h-16 sm:h-20 px-4 sm:px-6 lg:px-8">
          <Link to={localizedPath("/")} className="flex-shrink-0">
            <img src={logo} alt="ARCONDES" width={506} height={536} className="h-9 sm:h-11 w-auto transition-transform duration-300 hover:scale-105" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-7">
            {navKeys.map((link) => (
              <Link
                key={link.path}
                to={localizedPath(link.path)}
                className={`text-xs font-medium tracking-wide uppercase transition-colors hover:text-accent ${
                  isActive(link.path) ? "text-foreground" : "text-foreground/70"
                }`}
              >
                {t(`nav.${link.key}`)}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <LanguageSwitcher />
            <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 uppercase text-xs tracking-wider font-semibold px-6">
              <Link to={localizedPath("/contact")}>{t("nav.bookAssessment")}</Link>
            </Button>
          </div>

          {/* Mobile toggle */}
          <div className="lg:hidden flex items-center gap-3">
            <LanguageSwitcher />
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 text-foreground"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`lg:hidden bg-background border-b border-border overflow-hidden transition-all duration-300 ease-out ${
          mobileOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0 border-b-0"
        }`}
      >
        <div className="px-4 py-4 space-y-3">
          {navKeys.map((link, i) => (
            <Link
              key={link.path}
              to={localizedPath(link.path)}
              onClick={() => setMobileOpen(false)}
              className={`block text-sm font-medium uppercase tracking-wide py-2 transition-all duration-300 ${
                isActive(link.path) ? "text-foreground" : "text-foreground/70"
              }`}
              style={{
                transitionDelay: mobileOpen ? `${i * 40}ms` : "0ms",
                opacity: mobileOpen ? 1 : 0,
                transform: mobileOpen ? "translateX(0)" : "translateX(-10px)",
              }}
            >
              {t(`nav.${link.key}`)}
            </Link>
          ))}
          <div
            className="pt-4 border-t border-border space-y-3 transition-all duration-300"
            style={{
              transitionDelay: mobileOpen ? `${navKeys.length * 40}ms` : "0ms",
              opacity: mobileOpen ? 1 : 0,
            }}
          >
            <a href="tel:+13476131717" className="flex items-center gap-2 text-sm font-medium">
              <Phone className="h-4 w-4" />
              347-613-1717
            </a>
            <Button asChild className="w-full bg-accent text-accent-foreground hover:bg-accent/90 uppercase text-xs tracking-wider font-semibold">
              <Link to={localizedPath("/contact")} onClick={() => setMobileOpen(false)}>{t("nav.bookAssessment")}</Link>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
